import React, { useEffect, useRef, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import axios from 'axios';
import apiService, {
  Provider,
  Procedure,
  Operatory,
  CalendarAutoImportCatalogEntry,
  CalendarAutoImportCalendarConfig,
  CalendarAutoImportRunSummary,
  CalendarAutoImportSettings,
} from '../../services/apiService';
import { useToast } from '../../contexts/ToastContext';

interface GCalSettings {
  calendarId: string;
  impersonateAs: string | null;
  serviceAccountEmail: string | null;
  serviceAccountProjectId: string | null;
  serviceAccountClientId: string | null;
  isReady: boolean;
  lastError: string | null;
  source: 'settings_file' | 'env' | 'default';
  envFallback: string | null;
  secondaryCalendarId: string | null;
  secondarySource: 'settings_file' | 'env' | 'unset';
  secondaryEnvFallback: string | null;
  extraCalendarIds: string[];
  extraSource: 'settings_file' | 'env' | 'unset';
}

// Live-Sync-Status eines einzelnen Kalenders (Ergebnis von /settings/google-calendar/test).
type CalHealth = {
  status: 'checking' | 'ok' | 'error';
  summary?: string;
  timeZone?: string;
  message?: string;
  mode?: 'read' | 'write'; // 'write' = echte Schreibprobe bestanden, 'read' = nur Lesetest
};

// Ein konfigurierter Kalender, der auf Sync geprüft wird.
type CalTarget = {
  key: string;                       // 'primary' | 'secondary' | <extra calendar id>
  target: string;                    // an die Test-Route übergebener Wert
  role: 'main' | 'legacy' | 'extra';
  id: string;                        // angezeigte Kalender-ID
};

// Baut die Liste der zu prüfenden Kalender aus den Settings — Reihenfolge wie im UI:
// neuer Hauptkalender (Schreiben) zuerst, dann bisheriger, dann Zusatzkalender.
const buildTargets = (s: GCalSettings): CalTarget[] => {
  const list: CalTarget[] = [];
  if (s.secondaryCalendarId) {
    list.push({ key: 'secondary', target: 'secondary', role: 'main', id: s.secondaryCalendarId });
  }
  list.push({ key: 'primary', target: 'primary', role: 'legacy', id: s.calendarId });
  (s.extraCalendarIds || []).forEach(id => list.push({ key: id, target: id, role: 'extra', id }));
  return list;
};

const GoogleCalendarSettings: React.FC = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [settings, setSettings] = useState<GCalSettings | null>(null);
  const [calendarId, setCalendarId] = useState('');
  const [impersonateAs, setImpersonateAs] = useState('');
  const [secondaryCalendarId, setSecondaryCalendarId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingSecondary, setSavingSecondary] = useState(false);
  const [newExtraId, setNewExtraId] = useState('');
  const [savingExtras, setSavingExtras] = useState(false);
  const [testingExtraId, setTestingExtraId] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [testingSecondary, setTestingSecondary] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [uploadingSA, setUploadingSA] = useState(false);
  const saFileInputRef = useRef<HTMLInputElement | null>(null);
  // Persistenter Sync-Status pro Kalender, automatisch beim Laden befüllt.
  const [health, setHealth] = useState<Record<string, CalHealth>>({});

  // --- Auto-Import eingebundener Kalender ---
  const [aiCalendars, setAiCalendars] = useState<CalendarAutoImportCatalogEntry[]>([]);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiBack, setAiBack] = useState(7);
  const [aiAhead, setAiAhead] = useState(120);
  const [aiConfigs, setAiConfigs] = useState<Record<string, CalendarAutoImportCalendarConfig>>({});
  const [aiLastRun, setAiLastRun] = useState<CalendarAutoImportSettings['lastRun']>(null);
  const [aiProviders, setAiProviders] = useState<Provider[]>([]);
  const [aiProcedures, setAiProcedures] = useState<Procedure[]>([]);
  const [aiOperatories, setAiOperatories] = useState<Operatory[]>([]);
  const [aiLoading, setAiLoading] = useState(true);
  const [aiSaving, setAiSaving] = useState(false);
  const [aiRunning, setAiRunning] = useState(false);
  const [aiRunSummary, setAiRunSummary] = useState<CalendarAutoImportRunSummary | null>(null);

  // Übersetzt die Behandlungs-Bezeichnung (kann string oder {de,es,en} sein) — wie im Event-Modal.
  const procName = (p: Procedure): string =>
    typeof p.name === 'string' ? p.name : ((p.name as any)?.de || (p.name as any)?.en || '?');

  // Ein Kalender ist nur importierbar, wenn alle drei Standardwerte gesetzt sind.
  const hasDefaults = (c?: CalendarAutoImportCalendarConfig | null): boolean =>
    !!c && c.provider_id != null && c.procedure_id != null && c.operatory_id != null;

  // Liest die Auto-Import-Einstellungen + Stammdaten und baut die Formularzustände auf.
  const loadAutoImport = async () => {
    setAiLoading(true);
    try {
      const [aiResp, prov, proc, oper] = await Promise.all([
        apiService.getCalendarAutoImportSettings(),
        apiService.getProviders(true),
        apiService.getProcedures(true),
        apiService.getOperatories(true),
      ]);
      setAiProviders(prov.data.data || []);
      setAiProcedures(proc.data.data || []);
      setAiOperatories(oper.data.data || []);
      if (aiResp.data?.success) {
        const { settings: s, calendars } = aiResp.data.data;
        setAiCalendars(calendars || []);
        setAiEnabled(!!s.enabled);
        setAiBack(s.windowDaysBack ?? 7);
        setAiAhead(s.windowDaysAhead ?? 120);
        setAiLastRun(s.lastRun || null);
        // Konfiguration pro bekanntem Kalender aufbauen — bestehende Werte übernehmen, sonst Defaults.
        const byId: Record<string, CalendarAutoImportCalendarConfig> = {};
        (s.calendars || []).forEach(c => { byId[c.calendarId] = c; });
        const next: Record<string, CalendarAutoImportCalendarConfig> = {};
        (calendars || []).forEach(cal => {
          const existing = byId[cal.calendarId];
          next[cal.calendarId] = existing || {
            calendarId: cal.calendarId,
            enabled: false,
            provider_id: null,
            procedure_id: null,
            operatory_id: null,
          };
        });
        setAiConfigs(next);
      } else {
        addToast(aiResp.data?.message || t('googleCalendar.autoImport.errorLoad'), 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || t('googleCalendar.autoImport.errorLoad'), 'error');
    } finally {
      setAiLoading(false);
    }
  };

  const updateAiConfig = (calendarId: string, patch: Partial<CalendarAutoImportCalendarConfig>) => {
    setAiConfigs(prev => ({
      ...prev,
      [calendarId]: { ...prev[calendarId], ...patch },
    }));
  };

  // Kalender, die importieren sollen, aber unvollständige Standardwerte haben → Warnung.
  const aiMissingDefaults = aiCalendars.filter(cal => {
    const c = aiConfigs[cal.calendarId];
    return c?.enabled && !hasDefaults(c);
  });

  const aiMissingDefaultsLabel = aiMissingDefaults
    .map(cal => cal.role === 'main' ? t('googleCalendar.autoImport.mainCalendarLabel') : (cal.label || cal.calendarId))
    .join(', ');

  const handleSaveAutoImport = async () => {
    setAiSaving(true);
    try {
      // Nur Kalender mit vollständigen Defaults dürfen als enabled gespeichert werden.
      const calendars: CalendarAutoImportCalendarConfig[] = aiCalendars.map(cal => {
        const c = aiConfigs[cal.calendarId] || {
          calendarId: cal.calendarId, enabled: false, provider_id: null, procedure_id: null, operatory_id: null,
        };
        return {
          calendarId: cal.calendarId,
          enabled: !!c.enabled && hasDefaults(c),
          provider_id: c.provider_id ?? null,
          procedure_id: c.procedure_id ?? null,
          operatory_id: c.operatory_id ?? null,
        };
      });
      const resp = await apiService.updateCalendarAutoImportSettings({
        enabled: aiEnabled,
        windowDaysBack: Number(aiBack),
        windowDaysAhead: Number(aiAhead),
        calendars,
      });
      if (resp.data?.success) {
        const s = resp.data.data;
        setAiEnabled(!!s.enabled);
        setAiBack(s.windowDaysBack ?? 7);
        setAiAhead(s.windowDaysAhead ?? 120);
        setAiLastRun(s.lastRun || null);
        const byId: Record<string, CalendarAutoImportCalendarConfig> = {};
        (s.calendars || []).forEach(c => { byId[c.calendarId] = c; });
        setAiConfigs(prev => {
          const next = { ...prev };
          Object.keys(next).forEach(id => { if (byId[id]) next[id] = byId[id]; });
          return next;
        });
        addToast(t('googleCalendar.autoImport.saved'), 'success');
      } else {
        addToast(resp.data?.message || t('googleCalendar.autoImport.errorSave'), 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || t('googleCalendar.autoImport.errorSave'), 'error');
    } finally {
      setAiSaving(false);
    }
  };

  const handleRunAutoImport = async () => {
    setAiRunning(true);
    setAiRunSummary(null);
    try {
      const resp = await apiService.runCalendarAutoImport(false);
      if (resp.data?.success) {
        setAiRunSummary(resp.data.data);
        addToast(t('googleCalendar.autoImport.runDone'), 'success');
        // lastRun aktualisieren, damit die Anzeige sofort stimmt.
        const sum = resp.data.data;
        setAiLastRun({
          at: new Date().toISOString(),
          converted: sum.converted,
          skipped: sum.skipped,
          errors: sum.errors?.length || 0,
        });
      } else {
        addToast(resp.data?.message || t('googleCalendar.autoImport.errorRun'), 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || t('googleCalendar.autoImport.errorRun'), 'error');
    } finally {
      setAiRunning(false);
    }
  };

  // Prüft EINEN Kalender gegen die Test-Route und schreibt das Ergebnis in `health`.
  // Gibt das Resultat zurück, damit Aufrufer zusätzlich einen Toast zeigen können.
  const checkTarget = async (key: string, target: string): Promise<{ ok: boolean; data?: any; message?: string }> => {
    setHealth(h => ({ ...h, [key]: { status: 'checking' } }));
    try {
      const resp = await apiService.post<any>('/settings/google-calendar/test', { target });
      if (resp.data?.success) {
        const d = resp.data.data;
        setHealth(h => ({ ...h, [key]: { status: 'ok', summary: d?.summary, timeZone: d?.timeZone, mode: d?.mode } }));
        return { ok: true, data: d };
      }
      const msg = resp.data?.message || t('googleCalendar.errorTest');
      setHealth(h => ({ ...h, [key]: { status: 'error', message: msg } }));
      return { ok: false, message: msg };
    } catch (e: any) {
      const msg = e?.response?.data?.message || t('googleCalendar.errorTest');
      setHealth(h => ({ ...h, [key]: { status: 'error', message: msg } }));
      return { ok: false, message: msg };
    }
  };

  // Prüft ALLE konfigurierten Kalender parallel. Ohne initialisierten Service
  // (kein gültiger Service-Account) ist kein Sync möglich → Status leeren.
  const checkAllHealth = (s: GCalSettings | null) => {
    if (!s || !s.isReady) { setHealth({}); return; }
    buildTargets(s).forEach(tg => { void checkTarget(tg.key, tg.target); });
  };

  const load = async () => {
    setLoading(true);
    try {
      const resp = await apiService.get<GCalSettings>('/settings/google-calendar');
      if (resp.data?.success) {
        setSettings(resp.data.data);
        setCalendarId(resp.data.data.calendarId);
        setImpersonateAs(resp.data.data.impersonateAs || '');
        setSecondaryCalendarId(resp.data.data.secondaryCalendarId || '');
        checkAllHealth(resp.data.data); // Sync-Status automatisch prüfen
      } else {
        addToast(resp.data?.message || t('googleCalendar.errorLoad'), 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || t('googleCalendar.errorLoad'), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); loadAutoImport(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  const handleSave = async () => {
    if (!calendarId.trim()) {
      addToast(t('googleCalendar.errorCalendarIdEmpty'), 'error');
      return;
    }
    setSaving(true);
    try {
      const resp = await apiService.put<GCalSettings>('/settings/google-calendar', {
        calendarId: calendarId.trim(),
        impersonateAs: impersonateAs.trim(),
      });
      if (resp.data?.success) {
        setSettings(resp.data.data);
        setImpersonateAs(resp.data.data.impersonateAs || '');
        checkAllHealth(resp.data.data);
        addToast(t('googleCalendar.settingsSaved'), 'success');
      } else {
        addToast(resp.data?.message || t('googleCalendar.errorSave'), 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || t('googleCalendar.errorSave'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    const r = await checkTarget('primary', 'primary');
    if (r.ok) addToast(t('googleCalendar.accessOk', { summary: r.data?.summary || 'Calendar', timeZone: r.data?.timeZone || '?' }), 'success');
    else addToast(r.message || t('googleCalendar.errorTest'), 'error');
    setTesting(false);
  };

  // Speichert NUR die secondary calendar ID — getrennt vom Hauptkalender-Save,
  // damit ein Versehen im Hauptkalender-Feld nichts am Bisherigen verändert.
  const handleSaveSecondary = async () => {
    setSavingSecondary(true);
    try {
      const resp = await apiService.put<GCalSettings>('/settings/google-calendar', {
        secondaryCalendarId: secondaryCalendarId.trim(),
      });
      if (resp.data?.success) {
        setSettings(resp.data.data);
        setSecondaryCalendarId(resp.data.data.secondaryCalendarId || '');
        checkAllHealth(resp.data.data);
        addToast(
          resp.data.data.secondaryCalendarId
            ? t('googleCalendar.secondarySaved', { id: resp.data.data.secondaryCalendarId })
            : t('googleCalendar.secondaryRemoved'),
          'success'
        );
      } else {
        addToast(resp.data?.message || t('googleCalendar.errorSave'), 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || t('googleCalendar.errorSave'), 'error');
    } finally {
      setSavingSecondary(false);
    }
  };

  const handleTestSecondary = async () => {
    setTestingSecondary(true);
    const r = await checkTarget('secondary', 'secondary');
    if (r.ok) addToast(t('googleCalendar.accessOk', { summary: r.data?.summary || 'Calendar', timeZone: r.data?.timeZone || '?' }), 'success');
    else addToast(r.message || t('googleCalendar.errorTest'), 'error');
    setTestingSecondary(false);
  };

  // Read-only Zusatzkalender: jede Änderung (Hinzufügen/Entfernen) wird sofort
  // als komplette Liste gespeichert — kein Dirty-State nötig.
  const saveExtras = async (next: string[]) => {
    setSavingExtras(true);
    try {
      const resp = await apiService.put<GCalSettings>('/settings/google-calendar', {
        extraCalendarIds: next,
      });
      if (resp.data?.success) {
        setSettings(resp.data.data);
        setNewExtraId('');
        checkAllHealth(resp.data.data);
        addToast(t('googleCalendar.extraSaved'), 'success');
      } else {
        addToast(resp.data?.message || t('googleCalendar.errorSave'), 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || t('googleCalendar.errorSave'), 'error');
    } finally {
      setSavingExtras(false);
    }
  };

  const handleAddExtra = () => {
    const id = newExtraId.trim();
    if (!id) return;
    if ((settings?.extraCalendarIds || []).includes(id)) {
      addToast(t('googleCalendar.extraAlreadyAdded'), 'error');
      return;
    }
    saveExtras([...(settings?.extraCalendarIds || []), id]);
  };

  const handleRemoveExtra = (id: string) => {
    saveExtras((settings?.extraCalendarIds || []).filter(x => x !== id));
  };

  const handleTestExtra = async (id: string) => {
    setTestingExtraId(id);
    const r = await checkTarget(id, id);
    if (r.ok) addToast(t('googleCalendar.accessOk', { summary: r.data?.summary || 'Calendar', timeZone: r.data?.timeZone || '?' }), 'success');
    else addToast(r.message || t('googleCalendar.errorTest'), 'error');
    setTestingExtraId(null);
  };

  const handleServiceAccountUpload = async (file: File) => {
    if (!file.name.toLowerCase().endsWith('.json')) {
      addToast(t('googleCalendar.errorNotJson'), 'error');
      return;
    }
    if (file.size > 32 * 1024) {
      addToast(t('googleCalendar.errorFileTooLarge'), 'error');
      return;
    }
    if (!window.confirm(t('googleCalendar.confirmReplaceSA', { name: file.name }))) return;

    setUploadingSA(true);
    try {
      const fd = new FormData();
      fd.append('serviceAccount', file);
      // Bypass apiService here: its axios instance forces Content-Type: application/json,
      // which prevents the browser from setting the multipart boundary header for FormData.
      const token = localStorage.getItem('admin_token');
      const base = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api';
      const resp = await axios.post<{ success: boolean; data: GCalSettings; message?: string }>(
        `${base}/settings/google-calendar/service-account`,
        fd,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (resp.data?.success) {
        setSettings(resp.data.data);
        setCalendarId(resp.data.data.calendarId);
        checkAllHealth(resp.data.data);
        addToast(t('googleCalendar.saActive', { email: resp.data.data.serviceAccountEmail }), 'success');
      } else {
        addToast(resp.data?.message || t('googleCalendar.errorUpload'), 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || t('googleCalendar.errorUpload'), 'error');
    } finally {
      setUploadingSA(false);
      if (saFileInputRef.current) saFileInputRef.current.value = '';
    }
  };

  const handleReset = async () => {
    if (!window.confirm(t('googleCalendar.confirmReset'))) return;
    setResetting(true);
    try {
      const resp = await apiService.delete<GCalSettings>('/settings/google-calendar');
      if (resp.data?.success) {
        setSettings(resp.data.data);
        setCalendarId(resp.data.data.calendarId);
        checkAllHealth(resp.data.data);
        addToast(t('googleCalendar.resetDone'), 'success');
      } else {
        addToast(resp.data?.message || t('googleCalendar.errorReset'), 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || t('googleCalendar.errorReset'), 'error');
    } finally {
      setResetting(false);
    }
  };

  if (loading) return <div className="bg-white rounded-lg shadow p-6 text-gray-500">{t('googleCalendar.loading')}</div>;
  if (!settings) return null;

  const sourceLabel = {
    settings_file: t('googleCalendar.sourcePersisted'),
    env: t('googleCalendar.sourceEnv'),
    default: t('googleCalendar.sourceDefault'),
  }[settings.source];

  return (
    <section className="bg-white rounded-lg shadow p-6 space-y-6">
      <div>
        <h2 className="text-lg font-bold text-gray-900">📅 {t('googleCalendar.title')}</h2>
        <p className="text-sm text-gray-600 mt-1">
          {t('googleCalendar.description')}
        </p>
      </div>

      {/* Status row */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
          settings.isReady ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'
        }`}>
          {settings.isReady ? `● ${t('googleCalendar.connected')}` : `● ${t('googleCalendar.notConnected')}`}
        </span>
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
          {t('googleCalendar.sourceLabel', { source: sourceLabel })}
        </span>
        {settings.lastError && (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
            ⚠️ {settings.lastError}
          </span>
        )}
      </div>

      {/* Sync-Status — beim Öffnen automatisch pro Kalender geprüft, damit auf
          einen Blick sichtbar ist, ob der Sync funktioniert. */}
      {(() => {
        const targets = buildTargets(settings);
        const anyChecking = Object.values(health).some(h => h?.status === 'checking');
        const roleLabel = (role: CalTarget['role']) =>
          role === 'main' ? t('googleCalendar.syncRoleMain')
            : role === 'legacy' ? t('googleCalendar.syncRoleLegacy')
            : t('googleCalendar.syncRoleExtra');
        return (
          <div className="border border-gray-200 rounded p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-bold text-gray-800">
                🔄 {t('googleCalendar.syncStatusTitle')}{' '}
                <span className="font-normal text-gray-400 text-xs">{t('googleCalendar.syncStatusSub')}</span>
              </h3>
              <button
                onClick={() => checkAllHealth(settings)}
                disabled={!settings.isReady || anyChecking}
                className="px-3 py-1.5 text-xs border border-gray-300 rounded font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              >
                {anyChecking ? t('googleCalendar.syncRechecking') : t('googleCalendar.syncRecheck')}
              </button>
            </div>

            {!settings.isReady ? (
              <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                ⛔ {t('googleCalendar.syncServiceDown')}
              </p>
            ) : targets.length === 0 ? (
              <p className="text-sm text-gray-500">{t('googleCalendar.syncNoCalendars')}</p>
            ) : (
              <ul className="space-y-1.5">
                {targets.map(tg => {
                  const h = health[tg.key];
                  const st = h?.status;
                  const dot = st === 'ok' ? 'bg-green-500'
                    : st === 'error' ? 'bg-red-500'
                    : st === 'checking' ? 'bg-amber-400 animate-pulse'
                    : 'bg-gray-300';
                  return (
                    <li key={tg.key} className="flex items-center gap-2.5 text-sm border border-gray-100 rounded px-2.5 py-1.5">
                      <span className={`inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ${dot}`} />
                      <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-600 whitespace-nowrap">
                        {roleLabel(tg.role)}
                      </span>
                      <span className="font-mono text-xs text-gray-700 truncate flex-1 min-w-0">{tg.id}</span>
                      <span className="text-xs whitespace-nowrap text-right">
                        {st === 'ok' ? (
                          <span className="text-green-700 font-semibold">
                            ✅ {h?.mode === 'write' ? t('googleCalendar.syncOkWrite')
                              : h?.mode === 'read' ? t('googleCalendar.syncOkRead')
                              : t('googleCalendar.syncOk')}
                            {h?.timeZone && <span className="font-normal text-gray-500"> · {h.timeZone}</span>}
                          </span>
                        ) : st === 'error' ? (
                          <span className="text-red-700 font-semibold" title={h?.message || ''}>❌ {t('googleCalendar.syncFailed')}</span>
                        ) : st === 'checking' ? (
                          <span className="text-amber-600">⏳ {t('googleCalendar.syncChecking')}</span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}

            {/* Fehlermeldungen ausführlich unter der Liste, damit der Grund sichtbar ist. */}
            {targets.some(tg => health[tg.key]?.status === 'error') && (
              <ul className="space-y-1 pt-1">
                {targets.filter(tg => health[tg.key]?.status === 'error').map(tg => (
                  <li key={tg.key} className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                    <span className="font-mono">{tg.id}</span>: {health[tg.key]?.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })()}

      {/* Service account — display + upload */}
      <div className="border border-gray-200 rounded p-4 space-y-3 bg-gray-50">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t('googleCalendar.activeServiceAccount')}</label>
            <input
              type="text"
              value={settings.serviceAccountEmail || t('googleCalendar.unknown')}
              readOnly
              className="w-full p-2 border border-gray-200 bg-white rounded text-sm text-gray-700 font-mono"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2 text-[11px] text-gray-600">
              <div>
                <span className="font-semibold text-gray-500">{t('googleCalendar.projectLabel')}</span>{' '}
                <span className="font-mono">{settings.serviceAccountProjectId || '–'}</span>
              </div>
              <div>
                <span className="font-semibold text-gray-500">{t('googleCalendar.clientIdLabel')}</span>{' '}
                <span className="font-mono">{settings.serviceAccountClientId || '–'}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="pt-3 border-t border-gray-200">
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t('googleCalendar.replaceServiceAccount')}</label>
          <p className="text-[11px] text-gray-500 mb-2">
            <Trans i18nKey="googleCalendar.replaceServiceAccountHint" components={{ code: <code /> }} />
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={saFileInputRef}
              type="file"
              accept="application/json,.json"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleServiceAccountUpload(f);
              }}
              disabled={uploadingSA}
              className="text-xs text-gray-700 file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-light-blue file:text-white hover:file:bg-opacity-90 file:cursor-pointer disabled:opacity-50"
            />
            {uploadingSA && <span className="text-xs text-gray-500">{t('googleCalendar.uploadingAuthenticating')}</span>}
          </div>
          <p className="text-[11px] text-amber-700 mt-2">
            ⚠️ <Trans i18nKey="googleCalendar.accessWaysHint" components={{ strong: <strong />, em: <em /> }} />
          </p>
        </div>
      </div>

      {/* NEW: secondary calendar — this is the new default target for new
          appointments. Rendered FIRST and prominently so admins see it as the
          primary configuration surface. */}
      <div className="border-2 border-green-200 bg-green-50/40 rounded p-4 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <label className="block text-xs font-bold text-green-900 uppercase tracking-wider">
            {t('googleCalendar.newMainCalendar')} <span className="font-normal lowercase text-green-700">{t('googleCalendar.newMainCalendarSub')}</span>
          </label>
          {settings.secondaryCalendarId && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-green-600 text-white">
              {t('googleCalendar.activeBadge')}
            </span>
          )}
        </div>
        <input
          type="text"
          value={secondaryCalendarId}
          onChange={(e) => setSecondaryCalendarId(e.target.value)}
          placeholder="info@seadent.es"
          className="w-full p-2 border border-green-300 rounded text-sm font-mono bg-white focus:ring-2 focus:ring-green-500 outline-none"
        />
        <p className="text-[11px] text-green-900">
          <Trans
            i18nKey="googleCalendar.newMainCalendarHint"
            values={{ email: settings.serviceAccountEmail || '…' }}
            components={{ code: <code className="bg-white px-1 mx-1 rounded font-mono text-[10px]" /> }}
          />
        </p>
        <div className="flex flex-wrap gap-2 pt-1">
          <button
            onClick={handleTestSecondary}
            disabled={testingSecondary || !settings.isReady || !settings.secondaryCalendarId}
            className="px-3 py-1.5 bg-white border border-green-600 text-green-800 text-sm rounded font-semibold disabled:opacity-40 hover:bg-green-100"
            title={!settings.secondaryCalendarId ? t('googleCalendar.saveFirstTitle') : ''}
          >
            {testingSecondary ? t('googleCalendar.testing') : t('googleCalendar.testConnection')}
          </button>
          <button
            onClick={handleSaveSecondary}
            disabled={savingSecondary || secondaryCalendarId.trim() === (settings.secondaryCalendarId || '')}
            className="px-4 py-1.5 bg-green-700 text-white text-sm rounded font-semibold disabled:opacity-50 hover:bg-green-800"
          >
            {savingSecondary ? t('googleCalendar.saving') : (secondaryCalendarId.trim() ? t('common.save') : t('googleCalendar.remove'))}
          </button>
        </div>
        {settings.secondaryEnvFallback && settings.secondarySource !== 'env' && (
          <p className="text-[11px] text-amber-700">
            <Trans
              i18nKey="googleCalendar.envFallbackAvailable"
              values={{ value: settings.secondaryEnvFallback }}
              components={{ code: <code className="bg-amber-50 px-1 rounded" /> }}
            />
          </p>
        )}
      </div>

      {/* Read-only Zusatzkalender — werden im Admin-Kalender nur angezeigt,
          niemals als Schreib-Ziel für Termine verwendet. */}
      <div className="border-2 border-indigo-200 bg-indigo-50/40 rounded p-4 space-y-2">
        <label className="block text-xs font-bold text-indigo-900 uppercase tracking-wider">
          {t('googleCalendar.extraCalendars')} <span className="font-normal lowercase text-indigo-700">{t('googleCalendar.extraCalendarsSub')}</span>
        </label>
        <p className="text-[11px] text-indigo-900">
          <Trans
            i18nKey="googleCalendar.extraCalendarsHint"
            values={{ email: settings.impersonateAs || settings.serviceAccountEmail || '…' }}
            components={{ code: <code className="bg-white px-1 mx-1 rounded font-mono text-[10px]" /> }}
          />
        </p>
        {(settings.extraCalendarIds || []).length > 0 && (
          <ul className="space-y-1.5">
            {settings.extraCalendarIds.map(id => (
              <li key={id} className="flex items-center gap-2 bg-white border border-indigo-200 rounded px-2 py-1.5">
                <span className="flex-1 text-sm font-mono text-gray-800 truncate">{id}</span>
                <button
                  onClick={() => handleTestExtra(id)}
                  disabled={testingExtraId === id || !settings.isReady}
                  className="px-2 py-1 text-xs border border-indigo-500 text-indigo-700 rounded font-semibold hover:bg-indigo-100 disabled:opacity-40"
                >
                  {testingExtraId === id ? t('googleCalendar.testing') : t('googleCalendar.testConnection')}
                </button>
                <button
                  onClick={() => handleRemoveExtra(id)}
                  disabled={savingExtras}
                  className="px-2 py-1 text-xs border border-red-300 text-red-700 rounded font-semibold hover:bg-red-50 disabled:opacity-40"
                >
                  {t('googleCalendar.remove')}
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          <input
            type="text"
            value={newExtraId}
            onChange={(e) => setNewExtraId(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAddExtra(); }}
            placeholder="janheiringhoff@googlemail.com"
            className="flex-1 min-w-[220px] p-2 border border-indigo-300 rounded text-sm font-mono bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
          />
          <button
            onClick={handleAddExtra}
            disabled={savingExtras || !newExtraId.trim()}
            className="px-4 py-1.5 bg-indigo-700 text-white text-sm rounded font-semibold disabled:opacity-50 hover:bg-indigo-800"
          >
            {savingExtras ? t('googleCalendar.saving') : t('googleCalendar.extraAdd')}
          </button>
        </div>
      </div>

      {/* Auto-Import eingebundener Kalender — übernimmt neue Events der
          konfigurierten Kalender automatisch als App-Termine. */}
      <div className="border-2 border-amber-200 bg-amber-50/40 rounded p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <label className="block text-xs font-bold text-amber-900 uppercase tracking-wider">
            🔁 {t('googleCalendar.autoImport.title')}
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={aiEnabled}
              onChange={(e) => setAiEnabled(e.target.checked)}
              className="h-4 w-4 accent-amber-600"
            />
            <span className="text-sm font-semibold text-amber-900">{t('googleCalendar.autoImport.enable')}</span>
          </label>
        </div>
        <p className="text-[11px] text-amber-900">{t('googleCalendar.autoImport.subtitle')}</p>

        {aiLoading ? (
          <p className="text-sm text-gray-500">{t('googleCalendar.loading')}</p>
        ) : (
          <>
            {/* Zeitfenster */}
            <div className="grid grid-cols-2 gap-3 max-w-xs">
              <div>
                <label className="block text-[11px] font-semibold text-amber-900 mb-1">{t('googleCalendar.autoImport.windowBack')}</label>
                <input
                  type="number"
                  min={0}
                  value={aiBack}
                  onChange={(e) => setAiBack(Number(e.target.value))}
                  className="w-full p-2 border border-amber-300 rounded text-sm bg-white focus:ring-2 focus:ring-amber-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-amber-900 mb-1">{t('googleCalendar.autoImport.windowAhead')}</label>
                <input
                  type="number"
                  min={0}
                  value={aiAhead}
                  onChange={(e) => setAiAhead(Number(e.target.value))}
                  className="w-full p-2 border border-amber-300 rounded text-sm bg-white focus:ring-2 focus:ring-amber-500 outline-none"
                />
              </div>
            </div>

            <p className="text-[11px] text-amber-900">{t('googleCalendar.autoImport.perCalendarHint')}</p>

            {/* Pro-Kalender-Konfiguration */}
            <ul className="space-y-2">
              {aiCalendars.map(cal => {
                const c = aiConfigs[cal.calendarId];
                const label = cal.role === 'main' ? t('googleCalendar.autoImport.mainCalendarLabel') : (cal.label || cal.calendarId);
                const incomplete = c?.enabled && !hasDefaults(c);
                return (
                  <li
                    key={cal.calendarId}
                    className={`bg-white border rounded px-3 py-2.5 space-y-2 ${incomplete ? 'border-red-300' : 'border-amber-200'}`}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!c?.enabled}
                        onChange={(e) => updateAiConfig(cal.calendarId, { enabled: e.target.checked })}
                        className="h-4 w-4 accent-amber-600 flex-shrink-0"
                        id={`ai-enable-${cal.calendarId}`}
                      />
                      <label htmlFor={`ai-enable-${cal.calendarId}`} className="text-sm font-semibold text-gray-800 cursor-pointer min-w-0 truncate">
                        {label}
                      </label>
                      {cal.role === 'extra' && (
                        <span className="font-mono text-[11px] text-gray-400 truncate min-w-0">{cal.calendarId}</span>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <select
                        value={c?.provider_id ?? ''}
                        onChange={(e) => updateAiConfig(cal.calendarId, { provider_id: e.target.value ? Number(e.target.value) : null })}
                        className="p-2 border border-gray-300 rounded text-sm bg-white"
                      >
                        <option value="">{t('googleCalendar.autoImport.providerPlaceholder')}</option>
                        {aiProviders.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                      <select
                        value={c?.procedure_id ?? ''}
                        onChange={(e) => updateAiConfig(cal.calendarId, { procedure_id: e.target.value ? Number(e.target.value) : null })}
                        className="p-2 border border-gray-300 rounded text-sm bg-white"
                      >
                        <option value="">{t('googleCalendar.autoImport.procedurePlaceholder')}</option>
                        {aiProcedures.map(p => <option key={p.id} value={p.id}>{procName(p)}</option>)}
                      </select>
                      <select
                        value={c?.operatory_id ?? ''}
                        onChange={(e) => updateAiConfig(cal.calendarId, { operatory_id: e.target.value ? Number(e.target.value) : null })}
                        className="p-2 border border-gray-300 rounded text-sm bg-white"
                      >
                        <option value="">{t('googleCalendar.autoImport.operatoryPlaceholder')}</option>
                        {aiOperatories.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                      </select>
                    </div>
                  </li>
                );
              })}
            </ul>

            {/* Warnung: aktiviert, aber Standardwerte unvollständig */}
            {aiMissingDefaults.length > 0 && (
              <p className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                ⚠️ {t('googleCalendar.autoImport.missingDefaultsWarning', { calendars: aiMissingDefaultsLabel })}
              </p>
            )}

            {/* Letzter Lauf */}
            <p className="text-[11px] text-gray-600">
              {aiLastRun
                ? `${t('googleCalendar.autoImport.lastRun', { at: new Date(aiLastRun.at).toLocaleString() })} · ${t('googleCalendar.autoImport.resultConverted', { count: aiLastRun.converted })} · ${t('googleCalendar.autoImport.resultSkipped', { count: aiLastRun.skipped })} · ${t('googleCalendar.autoImport.resultErrors', { count: aiLastRun.errors })}`
                : t('googleCalendar.autoImport.lastRunNever')}
            </p>

            {/* Ergebnis des aktuellen Laufs */}
            {aiRunSummary && (
              <div className="text-[11px] text-gray-700 bg-white border border-amber-200 rounded px-3 py-2 space-y-1">
                {aiRunSummary.globallyDisabled && <p className="text-amber-700">⚠️ {t('googleCalendar.autoImport.globallyDisabledHint')}</p>}
                {aiRunSummary.notReady && <p className="text-red-700">⛔ {t('googleCalendar.autoImport.notReadyHint')}</p>}
                <p className="font-semibold">
                  {t('googleCalendar.autoImport.resultConverted', { count: aiRunSummary.converted })} ·{' '}
                  {t('googleCalendar.autoImport.resultSkipped', { count: aiRunSummary.skipped })} ·{' '}
                  {t('googleCalendar.autoImport.resultErrors', { count: aiRunSummary.errors?.length || 0 })}
                </p>
                {(aiRunSummary.errors || []).length > 0 && (
                  <ul className="space-y-0.5">
                    {aiRunSummary.errors.map((err, i) => (
                      <li key={i} className="text-red-700">
                        {err.summary ? <span className="font-mono">{err.summary}</span> : null}
                        {err.summary ? ': ' : ''}{err.message}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-2 pt-1">
              <button
                onClick={handleRunAutoImport}
                disabled={aiRunning || !settings.isReady}
                className="px-3 py-1.5 bg-white border border-amber-600 text-amber-800 text-sm rounded font-semibold disabled:opacity-40 hover:bg-amber-100"
                title={!settings.isReady ? t('googleCalendar.serviceNotInitTitle') : ''}
              >
                {aiRunning ? t('googleCalendar.autoImport.running') : t('googleCalendar.autoImport.runNow')}
              </button>
              <button
                onClick={handleSaveAutoImport}
                disabled={aiSaving}
                className="px-4 py-1.5 bg-amber-700 text-white text-sm rounded font-semibold disabled:opacity-50 hover:bg-amber-800"
              >
                {aiSaving ? t('googleCalendar.autoImport.saving') : t('googleCalendar.autoImport.save')}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Calendar ID input — BISHERIGER Kalender. Bleibt unverändert, nur
          re-labeled, damit Admins wissen welcher welcher ist. */}
      <div className="border border-gray-200 rounded p-4 space-y-2">
        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">
          {t('googleCalendar.legacyCalendar')} <span className="font-normal lowercase text-gray-400">{t('googleCalendar.legacyCalendarSub')}</span>
        </label>
        <input
          type="text"
          value={calendarId}
          onChange={(e) => setCalendarId(e.target.value)}
          placeholder="xxxxxx@group.calendar.google.com"
          className="w-full p-2 border border-gray-300 rounded text-sm font-mono focus:ring-2 focus:ring-light-blue outline-none"
        />
        <p className="text-[11px] text-gray-500">
          {t('googleCalendar.legacyCalendarHint')}
        </p>
        {settings.envFallback && settings.source !== 'env' && (
          <p className="text-[11px] text-amber-700">
            <Trans
              i18nKey="googleCalendar.envFallbackAvailableReset"
              values={{ value: settings.envFallback }}
              components={{ code: <code className="bg-amber-50 px-1 rounded" /> }}
            />
          </p>
        )}
      </div>

      {/* Impersonate-As (Domain-Wide Delegation) */}
      <div>
        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
          {t('googleCalendar.impersonateAs')} <span className="font-normal lowercase text-gray-400">{t('googleCalendar.impersonateAsSub')}</span>
        </label>
        <input
          type="email"
          value={impersonateAs}
          onChange={(e) => setImpersonateAs(e.target.value)}
          placeholder={t('googleCalendar.impersonatePlaceholder')}
          className="w-full p-2 border border-gray-300 rounded text-sm font-mono focus:ring-2 focus:ring-light-blue outline-none"
        />
        <p className="text-[11px] text-gray-500 mt-1">
          <Trans
            i18nKey="googleCalendar.impersonateHint"
            values={{ clientId: settings.serviceAccountClientId || '…' }}
            components={{ code: <code /> }}
          />
        </p>
        {settings.impersonateAs && (
          <p className="text-[11px] text-green-700 mt-1">
            <Trans
              i18nKey="googleCalendar.impersonateActive"
              values={{ value: settings.impersonateAs }}
              components={{ code: <code className="bg-green-50 px-1 rounded" /> }}
            />
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-gray-100">
        <button
          onClick={handleReset}
          disabled={resetting || settings.source !== 'settings_file'}
          className="text-sm text-gray-600 hover:text-red-700 underline disabled:no-underline disabled:opacity-40 disabled:cursor-not-allowed"
          title={settings.source !== 'settings_file' ? t('googleCalendar.resetNotNeededTitle') : t('googleCalendar.resetTitle')}
        >
          {resetting ? t('googleCalendar.resetting') : t('googleCalendar.resetToDefault')}
        </button>

        <div className="flex gap-2">
          <button
            onClick={handleTest}
            disabled={testing || !settings.isReady}
            className="px-4 py-2 bg-light-blue text-white rounded font-semibold disabled:opacity-50 hover:bg-opacity-90"
            title={!settings.isReady ? t('googleCalendar.serviceNotInitTitle') : ''}
          >
            {testing ? t('googleCalendar.testing') : t('googleCalendar.testConnection')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving || (calendarId.trim() === settings.calendarId && impersonateAs.trim() === (settings.impersonateAs || ''))}
            className="px-5 py-2 bg-brand-primary text-white rounded font-semibold disabled:opacity-50 hover:bg-opacity-90"
            title={t('googleCalendar.saveLegacyTitle')}
          >
            {saving ? t('googleCalendar.saving') : t('googleCalendar.saveLegacy')}
          </button>
        </div>
      </div>
    </section>
  );
};

export default GoogleCalendarSettings;
